////////////
// IMPORT //
////////////

// Three.js & Graphics:
import { SensorData } from './SensorPoint.js';

// Utils:
import FVector from '@data/geometry/FVector.js';

// Filters:

import Layer from './Layer.js';

// Stores Zustand
import { SensorDataManager as RawSensorDatas } from '@data/Simulations/RawSensorDatas.js';
import { getViewer } from 'store/viewerStore';

export class Frame {
    protected readonly UID: string; // TODO when the id will be set via the constructed: protected readonly UID: string;
    private readonly name: string;

    parentLayer: Layer;

    averageDirection?: FVector;

    private rawSensorDatas: RawSensorDatas;

    constructor(parentLayer: Layer, frameName: string, sensorDataManager: RawSensorDatas) {
        this.parentLayer = parentLayer;
        this.name = frameName;
        this.UID = parentLayer.getID() + '-' + this.name;
        this.rawSensorDatas = sensorDataManager;
    }

    getID() {
        return this.UID;
    }

    forceSensorPointValues(scalarValues: number[], vectorialValues: FVector[]) {
        this.rawSensorDatas = new RawSensorDatas(new Float32Array(scalarValues), vectorialValues);
    }

    getAverageDirection(): FVector {
        if (this.averageDirection) return this.averageDirection;
        return this.computeAverageDirection();
    }

    updateShadows() {
        // Update light:
        const shadowBuilder = getViewer().shadowBuilder;
        if (this.parentLayer.isSun()) shadowBuilder?.updatePosition(this.getAverageDirection());
        else shadowBuilder?.updatePosition(shadowBuilder?.getYearlyDirection().mult(-1));
    }

    /**
     *
     * @returns
     */
    computeAverageDirection(): FVector {
        const result = new FVector();

        // Sum Raw vectors to avoid recursion
        for (const sd of this.getSensors()) {
            const v = sd.getVectorialResult();
            if (v) result.add(v);
        }

        result.normalize();
        this.averageDirection = result;
        return result;
    }

    computeVariability() {
        const numPoints = this.getSensors().length;
        let sum = 0;
        for (let sensorData of this.getSensors()) sum += sensorData.getScalarValue();

        const mean = sum / numPoints;

        let sumSquaredDiff = 0;

        for (let sensorData of this.getSensors()) sumSquaredDiff += Math.pow(sensorData.getScalarValue() - mean, 2);

        let variance = sumSquaredDiff / numPoints;
        let standardDeviation = Math.sqrt(variance);
        let coefficientOfVariation = standardDeviation / mean;

        return coefficientOfVariation;
    }

    getSummedValue() {
        return this.getSensors()
            .map((sensor) => sensor.getScalarValue())
            .reduce((acc, val) => acc + val, 0);
    }

    /////////////////////////
    // Getters and Setters //
    /////////////////////////

    getSensors(): SensorData[] {
        return this.rawSensorDatas?.getSensorData() || [];
    }

    getScalarMinMax(): [number, number] {
        return this.rawSensorDatas.getScalarMinMax();
    }

    getVectorialMinMax(): [number, number] {
        return this.rawSensorDatas.getVectorialMinMax();
    }

    getRawScalarValues(): Float32Array {
        return this.rawSensorDatas.getScalarValues();
    }

    getRawVectorialValues(): FVector[] {
        return this.rawSensorDatas.getVectorialValues();
    }

    getName(): string {
        return this.name;
    }
}
